import { ForwardedRef, ReactNode } from 'react'
import { Button as AriaButton, ButtonProps as AriaButtonProps } from 'react-aria-components'
import { Link, LinkProps } from 'react-router-dom'

const BASE =
	'box-border outline-none inline-grid select-none align-middle font-sans transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed'

const SIZES = {
	SMALL: 'py-1 px-3 text-xs',
	MEDIUM: 'py-2.5 px-6 text-xs',
	LARGE: 'py-3 px-7 text-sm',
	TEXT: 'text-xs',
	NONE: '',
} as const

const VARIANT = {
	PRIMARY:
		'bg-gradient-to-r from-blue-500 to-blue-600 font-bold text-white shadow-md rounded-lg hover:from-blue-500 hover:to-blue-700 focus:opacity-90 focus:shadow-none active:opacity-80 active:shadow-none',
	SECONDARY:
		'border border-blue-600 rounded-lg font-bold text-blue-600 hover:bg-blue-50 active:bg-blue-100',
	TEXT: 'font-bold text-blue-600 hover:underline focus:underline active:text-blue-700',
	INVISIBLE:
		'text-sm bg-transparent text-gray-700 bg-blue-50 disabled:border-gray-100 border border-gray-400 p-2 rounded focus:border-blue-300 invalid:border-red-300',
	DANGER: 'border border-rose-500 bg-white font-bold text-rose-600 rounded-lg transition-shadow shadow-sm hover:bg-rose-50 active:shadow-none',
} as const

const ACTIVE = {
	PRIMARY: 'opacity-80 shadow-none',
	SECONDARY: 'opacity-80 bg-blue-50',
	TEXT: 'text-blue-700 bg-blue-50',
	INVISIBLE: 'bg-blue-50',
	DANGER: 'bg-rose-100 shadow-none',
} as const

const SPAN = {
	DEFAULT: 'place-items-center text-center',
	FULL: 'items-center justify-stretch w-full',
} as const

type Props = {
	children: ReactNode
	isActive?: boolean
	size?: keyof typeof SIZES
	span?: keyof typeof SPAN
	variant?: keyof typeof VARIANT
	innerRef?: ForwardedRef<HTMLButtonElement>
}

export type ButtonProps = AriaButtonProps & Props

export function Button({
	children,
	className = '',
	isActive = false,
	isPending = false,
	size = 'MEDIUM',
	span = 'DEFAULT',
	variant = 'PRIMARY',
	innerRef,
	...props
}: ButtonProps) {
	return (
		<AriaButton
			className={`${BASE} ${SIZES[size]} ${VARIANT[variant]} ${SPAN[span]} ${className} ${isActive ? ACTIVE[variant] : ''}`}
			isPending={isPending}
			ref={innerRef}
			{...props}
		>
			<div
				className={`${isPending ? 'opacity-0' : ''} row-span-full col-span-full flex gap-2`}
			>
				{children}
			</div>
			{isPending ? <div className="row-span-full col-span-full">...</div> : null}
		</AriaButton>
	)
}

export function ButtonLink({
	className = '',
	isDisabled = false,
	isActive = false,
	size = 'MEDIUM',
	variant = 'PRIMARY',
	...props
}: LinkProps & Props & Pick<AriaButtonProps, 'isDisabled'>) {
	if (isDisabled) {
		return (
			<Button isDisabled size={size} variant={variant} className={className}>
				{props.children}
			</Button>
		)
	}
	return (
		<Link
			className={`${BASE} ${SIZES[size]} ${VARIANT[variant]} ${className} ${isActive ? ACTIVE[variant] : ''}`}
			{...props}
		/>
	)
}
